import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets, useFetchPost } from '@app/src/api/fetchHooks'
import useExportEchartsToPDF from '@app/src/components/ExportToPDF/useExportEchartsToPdf'
import FilterDisplayChip from '@app/src/components/FilterDisplayChip'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useGroupBySelector } from '@app/src/context/GroupBySelectorContext'
import useQueryFilters, { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import { useAmplitudeTimeSpentTracking } from '@app/src/hooks/useAmplitudeTimeSpentTracking'
import useLocalisedCountryName from '@app/src/hooks/useLocalisedCountryName'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import DataHubInsights from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/DataHubInsights'
import { useDataInsightsModal } from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/DataInsightsModalContext'
import DatahubDataInsightsFilters from '@app/src/pages/ResourceCollection/Filters/DatahubDataInsightsFilters'
import { AnswersGroupingType } from '@app/src/pages/ResourceCollection/Filters/StatisticsConstants'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import { ResponseDraftStatus } from '@app/src/types/resourceExplorer'
import { getCurrentTimestamp } from '@app/src/utils'
import { comparePeriods } from '@app/src/utils/getOrderedPeriods'
import { sortAlphabetically } from '@app/src/utils/helpers'
import { AmplitudeTrackingEvents, spendClassificationOrder } from '@app/src/wf-constants'
import { BarChartOutlined, ListAltOutlined } from '@mui/icons-material'
import { Box, Button, ButtonGroup, Grid, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { dataHubAllowedFilters } from '.'
import { Operators } from '../../../Filters/useFilters'
import type { DataInsightsQuestionsQuery, StatisticsForQuestionPerGroup } from '../../DataHubScene'
import type { DataHubInsightsDetailsHandle } from './Details/DataHubInsightsDetails'
import DataHubInsightsDetails from './Details/DataHubInsightsDetails'
import GroupByChip from './GroupByChip'
import type { ActionButton } from '@app/src/components/ActionButtons'

export const CATEGORY_ID_FILTER_NAME = 'response.request.subscriptions.target.categoryOptions.categoryId'

const questionFilters = [
  'requestItem.questionId',
  'requestItem.template.options.id',
  'response.request.questionnaireTemplateId',
  CATEGORY_ID_FILTER_NAME,
].map(q => q.toLowerCase())

type DataHubInsightsSceneProps = {
  tabs: ResourceCollectionSceneProps['tabs']
}

export enum InsightsSubTabs {
  INSIGHTS,
  DETAILS,
}

const DataHubInsightsScene: React.FC<DataHubInsightsSceneProps> = ({ tabs }) => {
  const { formatMessage } = useIntl()
  const { trackEvent } = useAmplitude()
  const [hasDetailsData, setHasDetailsData] = useState(false)
  const [activeTab, setActiveTab] = React.useState<InsightsSubTabs>(InsightsSubTabs.INSIGHTS)
  const appliedFiltersRef = useRef(null)
  const hasUserInteractedWithFilters = useRef(false)
  const { groupBy, groupByValue, setGroupBy } = useGroupBySelector()
  const { setQueryFilters } = useQueryFilters(dataHubAllowedFilters)
  const userFilters = useGetApiQueryFilters(dataHubAllowedFilters)
  const { generatePDF, isGeneratingPdf } = useExportEchartsToPDF()
  const { echartsRefs, clearEchartsRefOverIndex } = useDataInsightsModal()
  const { getLocalisedCountryName } = useLocalisedCountryName()
  const anyQuestionFilterSet = useMemo(
    () => userFilters.some(filter => questionFilters.includes(filter.name.toLowerCase())),
    [userFilters],
  )

  const detailsRef = useRef<DataHubInsightsDetailsHandle>(null)
  const filteredPeriodNames = useMemo(() => {
    const periodNameFilterValue = userFilters.find(x => x.name === 'response.request.periodName')?.filters[0]
      ?.value as string[]

    const safePeriodFilterValue = Array.isArray(periodNameFilterValue) ? periodNameFilterValue : [periodNameFilterValue]
    return safePeriodFilterValue?.sort((a, b) => comparePeriods(a, b))
  }, [userFilters])

  const hasQuestionnaireFilter = userFilters.some(filter => filter.name === 'response.request.questionnaireTemplateId')

  const isDetailsTabActive = activeTab === InsightsSubTabs.DETAILS

  useAmplitudeTimeSpentTracking({
    eventName: AmplitudeTrackingEvents.Analyze.DataHub.Insights.LeftPage,
    timerStarted: anyQuestionFilterSet,
  })

  const {
    facets: [availableQuestionnaireIds = []],
  } = useFetchFacets({
    key: [FetchKey.FilterFacet, 'questionnaireTemplateId'],
    endpoint: endpoints.requestsWithFacets,
    facetsParam: [{ name: 'questionnaireTemplateId', isEnum: true }],
    filter: [
      {
        name: 'responseDraftStatus',
        filters: [{ value: [ResponseDraftStatus.Submitted, ResponseDraftStatus.Scraped], operator: Operators.In }],
      },
    ],
    options: { staleTime: ONE_MINUTE_MS },
  })

  useEffect(() => {
    if (availableQuestionnaireIds.length && !hasUserInteractedWithFilters.current && !hasQuestionnaireFilter) {
      setQueryFilters([
        {
          name: 'response.request.questionnaireTemplateId',
          value: [availableQuestionnaireIds.sort((a, b) => b.count - a.count)[0].value],
          operator: Operators.In,
        },
      ])
    }
  }, [availableQuestionnaireIds, userFilters, setQueryFilters])

  // Detect when user manually changes filters
  useEffect(() => {
    if (!hasQuestionnaireFilter && hasUserInteractedWithFilters.current) {
      // User has manually removed the questionnaire filter
      hasUserInteractedWithFilters.current = true
    }
  }, [userFilters])

  // Detect when user manually adds filters
  useEffect(() => {
    if (userFilters.length > 0 && !hasUserInteractedWithFilters.current) {
      hasUserInteractedWithFilters.current = true
    }
  }, [userFilters])

  const { data: questions, isLoading } = useFetchPost<StatisticsForQuestionPerGroup[], DataInsightsQuestionsQuery>({
    payload: {
      filter: [...userFilters],
      groupBy,
      groupByValue: groupByValue,
    },
    endpoint: endpoints.dataInsightsByQuestions,
    key: [FetchKey.ResponseItemDataInsights],
    options: { enabled: anyQuestionFilterSet },
  })

  const preparedQuestions = useMemo(() => {
    if (groupBy === AnswersGroupingType.SpendClassification && questions) {
      return questions.map(question => ({
        ...question,
        resultForGroups: question.resultForGroups.sort((a, b) => {
          return spendClassificationOrder.indexOf(a.groupName) - spendClassificationOrder.indexOf(b.groupName)
        }),
      }))
    }
    if (groupBy === AnswersGroupingType.Country && questions) {
      return questions.map(question => ({
        ...question,
        resultForGroups: sortAlphabetically(
          question.resultForGroups.map(x => ({
            ...x,
            groupName: getLocalisedCountryName(x.groupName),
          })),
          item => item.groupName,
        ),
      }))
    }
    return questions
  }, [questions, groupBy])

  useEffect(() => {
    clearEchartsRefOverIndex(preparedQuestions?.length ?? 0)
  }, [clearEchartsRefOverIndex, preparedQuestions?.length])

  const exportChartsToPdf = async () => {
    trackEvent({
      name: AmplitudeTrackingEvents.Analyze.DataHub.Insights.PdfExport,
    })
    const timestamp = getCurrentTimestamp()
    const pdfFileName = `DataInsights_${timestamp}.pdf`

    const dataToExport =
      preparedQuestions?.flatMap((question, index) => {
        const echartsRef = echartsRefs?.[index]?.current
        if (!echartsRef) return []
        return { title: question.title, echarts: echartsRef }
      }) ?? []
    await generatePDF(pdfFileName, dataToExport, appliedFiltersRef)
  }

  const disableExport =
    !anyQuestionFilterSet || !preparedQuestions?.length || echartsRefs.every(r => !r?.current) || isDetailsTabActive

  const actionButtons = useMemo(() => {
    const buttons: ActionButton[] = [
      {
        label: formatMessage({ id: 'resourceCollections.general.exportToPdf' }),
        variant: 'outlined',
        onClick: exportChartsToPdf,
        disabled: disableExport,
        loading: isGeneratingPdf,
      },
    ]

    if (isDetailsTabActive) {
      buttons.unshift({
        label: formatMessage({ id: 'resourceCollections.general.export' }),
        variant: 'outlined',
        onClick: () => detailsRef.current?.triggerCustomAction(),
        disabled: !hasDetailsData,
      })
    }

    return buttons
  }, [isDetailsTabActive, disableExport, isGeneratingPdf, exportChartsToPdf, hasDetailsData])

  return (
    <ResourceCollectionScene
      title={formatMessage({
        id: 'schemas.responseItem.responseItems',
      })}
      tabs={tabs}
      filter={
        <DatahubDataInsightsFilters
          allowedFilters={dataHubAllowedFilters}
          anyQuestionFilterSet={anyQuestionFilterSet}
          isDetailsTabActive={isDetailsTabActive}
        />
      }
      actionButtons={actionButtons}
      enableScroll
    >
      <Box display="flex" py={3} flexDirection="row-reverse">
        <Stack alignItems="center" direction="row">
          <ButtonGroup sx={{ mr: 4 }}>
            <Button
              startIcon={<BarChartOutlined />}
              onClick={() => {
                setActiveTab(InsightsSubTabs.INSIGHTS)
              }}
              variant={activeTab === InsightsSubTabs.INSIGHTS ? 'contained' : 'outlined'}
            >
              {formatMessage({ id: 'resourceTypes.insight' })}
            </Button>
            <Button
              startIcon={<ListAltOutlined />}
              onClick={() => {
                setActiveTab(InsightsSubTabs.DETAILS)
                setGroupBy(AnswersGroupingType.PeriodName)
              }}
              variant={isDetailsTabActive ? 'contained' : 'outlined'}
            >
              {formatMessage({ id: 'general.details' })}
            </Button>
          </ButtonGroup>
        </Stack>
      </Box>
      {isDetailsTabActive ? (
        <DataHubInsightsDetails ref={detailsRef} onDataChange={setHasDetailsData} />
      ) : (
        <DataHubInsights
          key={JSON.stringify(userFilters)}
          questions={preparedQuestions}
          isLoading={isLoading}
          activeTab={activeTab}
          periodNames={filteredPeriodNames}
        />
      )}
      {Boolean(userFilters?.length) && (
        <Box ref={appliedFiltersRef}>
          <Grid container>
            <Grid item md={6} xs={12}>
              <Stack direction="row" spacing={1} my={2}>
                <Typography whiteSpace="nowrap" pt={0.5}>
                  {formatMessage({ id: 'schemas.filter.filtersApplied' })}:
                </Typography>
                <Stack direction="row" ml={-1} flexWrap="wrap">
                  <GroupByChip />
                  {userFilters.map(filter => (
                    <FilterDisplayChip
                      key={`${filter.name}-${filter.filters[0].operator}`}
                      filterName={filter.name}
                      filterValue={filter.filters[0].value}
                      operator={filter.filters[0].operator}
                    />
                  ))}
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}
    </ResourceCollectionScene>
  )
}

export default DataHubInsightsScene
